/* JCI mobile filter drawer behaviour.
 *
 * Checkboxes accumulate locally - no request is sent per tap. "Show
 * results" builds one combined URL from every checked box (grouped by
 * filter param) and dispatches the theme's own `facet:criteria-changed`
 * event at #facet-filters, which is what jci-product-listing.js already
 * does for the desktop Make/Model/Type dropdowns - so this reuses the
 * exact same native ProductFacet AJAX refresh, just batched.
 *
 * Picking a Make also narrows the Model tab immediately (client-side, via
 * the static window.JCIModelMakeMap - see jci-model-make-map.js) so the
 * list shortens the instant you pick a brand, without waiting on a
 * request. Server-rendered counts stay as-is until Apply.
 */
(() => {
  const ROOT_SELECTOR = '[data-jci-mobile-filters]';

  const init = (root) => {
    if (!root || root.dataset.jciMfInitialized === 'true') return;
    root.dataset.jciMfInitialized = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-jci-mf-tab]'));
    const panes = Array.from(root.querySelectorAll('[data-jci-mf-pane]'));
    const applyButton = root.querySelector('[data-jci-mf-apply]');
    const clearButton = root.querySelector('[data-jci-mf-clear]');
    const modelHint = root.querySelector('[data-jci-mf-hint="model"]');
    const modelMap = window.JCIModelMakeMap || {};
    const lockedMake = (root.dataset.jciMfLockedMake || '').trim();
    // Make-labels on the Model rows are applied by jci-product-listing.js's
    // syncControls (shared with the desktop dropdown) - nothing to do here.

    /* ---- tabs ---- */
    const selectTab = (key) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.jciMfTab === key)));
      panes.forEach((p) => { p.hidden = p.dataset.jciMfPane !== key; });
      const list = root.querySelector(`[data-jci-mf-list="${key}"]`);
      if (list) list.scrollTop = 0;
    };
    tabs.forEach((t) => t.addEventListener('click', () => selectTab(t.dataset.jciMfTab)));

    /* ---- search-within-filter ---- */
    root.querySelectorAll('[data-jci-mf-search]').forEach((input) => {
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        const key = input.dataset.jciMfSearch;
        const list = root.querySelector(`[data-jci-mf-list="${key}"]`);
        if (!list) return;
        list.querySelectorAll('[data-jci-mf-name]').forEach((row) => {
          row.hidden = q !== '' && row.dataset.jciMfName.indexOf(q) === -1;
        });
      });
    });

    /* ---- Make narrows Model (client-side, instant) ---- */
    const syncModelAvailability = () => {
      const checkedMakes = Array.from(root.querySelectorAll('[data-jci-mf-input="make"]:checked'))
        .map((c) => c.closest('[data-jci-mf-value]').dataset.jciMfValue);
      if (!checkedMakes.length && lockedMake) checkedMakes.push(lockedMake);

      root.querySelectorAll('[data-jci-mf-model]').forEach((row) => {
        if (!checkedMakes.length) { row.hidden = false; return; }
        // A model can legitimately belong to more than one make (shared
        // platforms/badge-engineered twins - see jci-model-make-map.js), so
        // keep it visible if ANY checked make matches, not just the first.
        const makes = modelMap[row.dataset.jciMfModel];
        row.hidden = !makes || !makes.some((make) => checkedMakes.indexOf(make) > -1);
      });

      if (modelHint) {
        modelHint.textContent = checkedMakes.length
          ? `Showing models for ${checkedMakes.join(', ')}`
          : 'Pick a make to shorten the list';
      }
    };
    syncModelAvailability();

    /* ---- badges + footer count ---- */
    const groupCount = (key) => root.querySelectorAll(`[data-jci-mf-input="${key}"]:checked`).length;

    const refreshBadges = () => {
      ['make', 'model', 'type'].forEach((key) => {
        const n = groupCount(key);
        const badge = root.querySelector(`[data-jci-mf-badge="${key}"]`);
        if (badge) { badge.hidden = n === 0; badge.textContent = n; }
      });
      const total = groupCount('make') + groupCount('model') + groupCount('type');
      clearButton.hidden = total === 0;
      applyButton.textContent = total > 0 ? `Show results (${total})` : 'Show results';
    };

    root.addEventListener('change', (event) => {
      if (!event.target.matches('[data-jci-mf-input]')) return;
      if (event.target.dataset.jciMfInput === 'make') syncModelAvailability();
      refreshBadges();
    });

    /* ---- apply: build one combined URL from every checked box ---- */
    const buildUrl = () => {
      const base = new URL(root.dataset.baseUrl, window.location.origin);
      const params = base.searchParams;
      root.querySelectorAll('[data-jci-mf-input]:checked').forEach((box) => {
        params.append(box.name, box.value);
      });
      return base.pathname + '?' + params.toString();
    };

    const dispatchCriteria = (url) => {
      const facetFilters = document.getElementById('facet-filters');
      if (!facetFilters) { window.location.href = url; return; }
      facetFilters.dispatchEvent(new CustomEvent('facet:criteria-changed', {
        bubbles: true,
        detail: { url },
      }));
    };

    applyButton.addEventListener('click', () => {
      dispatchCriteria(buildUrl());
      root.open = false;
    });

    clearButton.addEventListener('click', () => {
      root.querySelectorAll('[data-jci-mf-input]:checked').forEach((box) => { box.checked = false; });
      dispatchCriteria(root.dataset.baseUrl);
      root.open = false;
    });

    refreshBadges();
  };

  const boot = () => document.querySelectorAll(ROOT_SELECTOR).forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* The drawer lives inside #facet-main, so ProductFacet's AJAX refresh
     (triggered by facet:criteria-changed) replaces it with a fresh,
     un-initialized copy - but that replacement happens after an awaited
     fetch, well after criteria-changed fires, so listening for that event
     directly re-inits too early (against the about-to-be-discarded old
     node). jci-product-listing.js already runs a MutationObserver on
     #facet-main for exactly this kind of post-swap work; hook into the
     same signal instead of racing a second one. */
  window.JCIMobileFilters = { boot };
})();
